import React, { useState, useEffect } from "react";
import {
useTheme,
Box,
Paper,
Grid,
Button,
TextField,
Typography,
ButtonGroup,
Popover,
IconButton,
Select,
MenuItem,
FormControl,
ListItemIcon,
FormControlLabel,
InputLabel,
Checkbox,
Dialog,
DialogContent,
DialogActions,
Alert,
} from "@mui/material";
import { Formik } from "formik";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import "../../i18n";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { TimeField } from "@mui/x-date-pickers/TimeField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

// Components
import TitleHeader from "../../components/TitleHeader";
import Loading from "../../components/Loading";
import BreadcrumbsNav from "../../components/BreadcrumbsNav";
import useToast from "../../components/useToast";
import { tokens } from "../../theme";

// Icons
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { ThreeDots } from "react-loader-spinner";

// Others
import * as yup from "yup";
import { API_BASE_URL } from "../../config";

// --- Time + validation helpers ---
const timeToMin = (t) => {
  if (!t || !/^\d{2}:\d{2}$/.test(t)) return null;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};

const openCloseInvalid = (open, close) =>
  open && close && timeToMin(open) != null && timeToMin(close) != null
    ? timeToMin(open) === timeToMin(close)
    : false;

const breakInvalid = (open, close, bStart, bEnd) =>
  open && close && bStart && bEnd &&
  [open, bStart, bEnd, close].every((x) => timeToMin(x) != null)
    ? !(timeToMin(open) < timeToMin(bStart) &&
        timeToMin(bStart) < timeToMin(bEnd) &&
        timeToMin(bEnd) < timeToMin(close))
    : false;

// robust HH:mm <-> dayjs conversion without plugins
const toDayjs = (s) => (s ? dayjs(`2000-01-01T${s}`) : null);
const fromDayjs = (v) => (v && v.isValid() ? v.format("HH:mm") : "");


const AvailabilityTemp = ({ availability }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Mobile -> Check wann man als Mobile Gerät definiert ist + Bildschirmgrösse
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [availabilityData, setAvailabilityData] = useState({});
  const [openingData, setOpeningData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [weekAdjustment, setWeekAdjustment] = useState(0);
  const [additionalTimes, setAdditionalTimes] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [activeTemplateData, setActiveTemplateData] = useState({});
  const [selectedUser, setSelectedUser] = useState("");
  const [user_list, setUserList] = useState([]);
  const [checkedBoxes, setCheckedBoxes] = useState([]);
  const { t, i18n } = useTranslation();
  const [selectedOption, setSelectedOption] = useState('info'); // info als Standardansicht
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [originalAvailability, setOriginalAvailability] = useState({});
  const [originalAdditionalTimes, setOriginalAdditionalTimes] = useState(0);
  const [anchorElOptions, setAnchorElOptions] = useState(null);
  const [templateNameList, setTemplateNameList] = useState([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const[templateName, setTemplateName] = useState("")
  const[newTemplateName, setNewTemplateName] = useState("")
  const [breakOpen, setBreakOpen] = useState([]);
  const navigate = useNavigate();
  const { success, error, info, loading, close } = useToast();



  const token = localStorage.getItem("session_token");

  const handleDateChange = (date) => {
    // Close DatePicker
    setDatePickerOpen(false);

    // Find the selected Monday
    const dayOfWeek = date.getDay();
    const selectedMonday = new Date(date);
    selectedMonday.setDate(selectedMonday.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));

    // Assuming you store the current Monday based on `weekAdjustment` in state or can calculate it
    const currentMonday = new Date(availabilityData.week_start);

    // Calculate the difference in weeks between the selected Monday and the current Monday
    const differenceInWeeks = Math.round((selectedMonday - currentMonday) / ( 24 * 60 * 60 * 1000));

    // Update weekAdjustment accordingly
    setWeekAdjustment(weekAdjustment + differenceInWeeks);
  };


  useEffect(() => {
    if (
      selectedTemplate &&
      selectedTemplate !== "new_template" &&
      availabilityData.template_dict
    ) {
      setActiveTemplateData(availabilityData.template_dict);
    } else {
      // No template or "new_template": blank template
      setActiveTemplateData({});
    }
  }, [availabilityData, selectedTemplate]);


  useEffect(() => {
    // GET Anfrage
    const fetchAvailabilityData = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/availability/web`, {
            params: {
              week_adjustment: weekAdjustment,
              selectedUser: selectedUser,
              selectedTemplate: selectedTemplate,
            },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const data = response.data;
        setAvailabilityData(response.data);
        setUserList(response.data.user_list);
        setTemplateNameList(response.data.availability_template_names);
        
        setIsLoading(false);
        // console.log(response);
      } catch (error) {
        // console.error("Error fetching availability details:", error);
        setIsLoading(false);
      }
    };

    fetchAvailabilityData();
  }, [weekAdjustment, token, selectedUser, selectedTemplate]);


  useEffect(() => {
    if (availabilityData.temp_dict && availabilityData.day_num) {
      let foundEndTime2 = false;

      // Iterieren über jeden Tag, um zu prüfen, ob eine end_time2 vorhanden ist
      for (let day = 1; day <= availabilityData.day_num; day++) {
        //  &3 -> end_time2
        if (availabilityData.temp_dict[`${day}&3`]) {
          foundEndTime2 = true;
          break;
        }
      }
      setAdditionalTimes(foundEndTime2 ? 1 : 0);
    }
  }, [availabilityData]);



  useEffect(() => {
    if (selectedUser) {
      setAvailabilityData({});
      setActiveTemplateData({});
    }
  }, [selectedUser]);


  const hourDivider = availabilityData.hourDivider || 2;
  const step = 3600 / hourDivider;

  const handleAddTime = () => {
    if (additionalTimes < 1) {
      setAdditionalTimes(additionalTimes + 1);
    }
  };

  // POST Anfrage
  const handleFormSubmit = async (values, buttonName) => {
    setIsLoading(true);
    Object.keys(values).forEach((key) => {
      // Check if the key is a time field
      if (key.startsWith("day_") && values[key] === "") {
        // Set the value to None if the field is empty
        values[key] = undefined;
      }
    });
    const payload = {
      ...values,
      button: buttonName,
      selectedTemplate,
      templateName,
      newTemplateName,
      selectedUser,
    };

    // console.log(payload)
    // console.log("Final payload before sending to server:", payload);

    try {
      // Send the updated form values to the server for database update
      await axios.post(
        `${API_BASE_URL}/api/availability/web?week_adjustment=${weekAdjustment}&selectedUser=${encodeURIComponent(
          selectedUser
        )}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const response = await axios.get(
        `${API_BASE_URL}/api/availability/web`, {
          params: {
            week_adjustment: weekAdjustment,
            selectedUser: selectedUser,
            selectedTemplate: selectedTemplate,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = response.data;
      setAvailabilityData(response.data);
      setUserList(response.data.user_list);
      setTemplateNameList(response.data.availability_template_names);
      
      
      setSelectedTemplate("");
      setNewTemplateName("");
      success(t("notification.successful"));
      
      setIsLoading(false);
    } catch (error) {
      console.error("Error updating availability details:", error);
      error(t("notification.error"));
      setIsLoading(false);
    }
  };


  const DeletionDialog = (
    <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
      <DialogContent>
        {t('planning.deletion_confirmation')}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenConfirmDialog(false)}>{t("cancel")}</Button>
        <Button onClick={() => {
          setOpenConfirmDialog(false);
          handleFormSubmit("", "Delete Template");
        }} color="primary">{t("confirm")}</Button>
      </DialogActions>
    </Dialog>
  );



  return (
    <Box m={2}>
      <Loading open={isLoading} />
      <BreadcrumbsNav
        items={[
          { label: t("availabilty.title") || "Availability", to: "/availability" },
          { label: t("planning.create_template") || "Availability Template" },
        ]}
      />

      {DeletionDialog}

      <Formik
        onSubmit={handleFormSubmit}
        enableReinitialize={true}
        initialValues={{
          absenceCheckbox: false,
          ...Array.from({ length: availabilityData.day_num || 0 }).reduce(
            (acc, _, rowIndex) => {
              acc[`day_${rowIndex}_0`] =
                activeTemplateData && activeTemplateData[`${rowIndex + 1}&0`];
              acc[`day_${rowIndex}_1`] =
                activeTemplateData && activeTemplateData[`${rowIndex + 1}&1`];
              acc[`day_${rowIndex}_2`] =
                activeTemplateData && activeTemplateData[`${rowIndex + 1}&2`];
              acc[`day_${rowIndex}_3`] =
                activeTemplateData && activeTemplateData[`${rowIndex + 1}&3`];
              acc[`day_${rowIndex}_4`] =
                activeTemplateData && activeTemplateData[`${rowIndex + 1}&4`];
              acc[`day_${rowIndex}_5`] =
                activeTemplateData && activeTemplateData[`${rowIndex + 1}&5`];
              return acc;
            },
            {}
          ),
        }}
        validationSchema={checkoutSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <TitleHeader
              title={t("planning.create_template")}
              subtitle={t("planning.create_template_subtitle")}
            />
            <Paper
              elevation={5}
              sx={{
                p: 2,
                bgcolor: "background.box",
                //border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box
                sx={{
                  maxHeight: 'calc(100vh - 100px)', 
                  overflowY: 'auto',
                  p: 2,
                  bgcolor: "background.box",
                  borderRadius: 1,
                }}
              >


              {/* Left-aligned items */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  mb: 2,
                  background: "white",
                  borderRadius: 1,
                  border: "1px solid #e0e0e0",
                  boxShadow: "0px 0px 10px rgba(0,0,0,0.08)",
                  p: 1,
                }}
              >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, m: 1 }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="template-select-label">{t("planning.select_template")}</InputLabel>
                  <Select
                    type="text"
                    label={t("planning.select_template")}
                    name="template_name"
                    value={selectedTemplate}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (value === "no_template") return;

                      setSelectedTemplate(value);

                      if (value === "new_template") {
                        setTemplateName("");
                        setNewTemplateName("");
                      }
                    }}
                    onClick={(e) => {
                      if (templateNameList.length === 0) {
                        e.preventDefault();
                      }
                    }}
                    renderValue={(selected) => {
                      if (selected === "new_template") {
                          return t("planning.new_template");
                      }
                      return selected;
                    }}
                    sx={{
                        color: "black",
                        backgroundColor: 'white',
                        marginRight: "0.2rem",
                        height: "35px", // explicitly set height
                        ".MuiInputBase-root": {
                            height: "35px", // explicitly set input field height
                            fontSize: "10px", // explicitly set font size
                            backgroundColor: 'white',
                        },
                        ".MuiSelect-icon": {
                            // Change color of icon to black
                            color: "black",
                        },
                    }}
                  >
                    <MenuItem value="new_template">
                      <ListItemIcon>
                        <AddIcon fontSize="small" sx={{ color: "#22E3B6" }} />
                      </ListItemIcon>
                      <Typography sx={{ color: "#22E3B6" }}>{t("planning.new_template")}</Typography>
                    </MenuItem>

                    {templateNameList.map((name) => (
                      <MenuItem key={name} value={name}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {selectedTemplate==="new_template" && (
                  <TextField
                    placeholder={t("planning.template_name")}
                    variant="outlined"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    fullWidth
                    InputProps={{  
                      style: {
                        height: 35, 
                      }
                    }}
                    inputProps={{ 
                      min: 0,
                      style: {
                        padding: '10px 14px',  
                        backgroundColor: 'white',
                      }
                    }}
                  />
                  )}
                  {selectedTemplate!=="" && selectedTemplate!=="new_template" && (
                  <TextField
                    placeholder={t("planning.template_name_change")}
                    variant="outlined"
                    value={newTemplateName}
                    onChange={(e) => setNewTemplateName(e.target.value)}
                    fullWidth
                    InputProps={{  
                      style: {
                        height: 35, 
                      }
                    }}
                    inputProps={{ 
                      min: 0,
                      style: {
                        padding: '10px 14px',  
                        backgroundColor: 'white',
                      }
                    }}
                  />
                )}
                {selectedTemplate!=="" && selectedTemplate!=="new_template" && (
                  <IconButton
                      onClick={() => setOpenConfirmDialog(true)}
                      sx={{ ml: 'auto' }}
                    >
                      <DeleteIcon sx={{ color: 'red' }} />
                    </IconButton>
                )}  
              </Box>


              {/* Right-aligned items */}
              {/* Wenn nicht Mobile: */}
              {isNonMobile && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, m: 1 }}>
                <Button
                  onClick={() => handleFormSubmit(values, "Save Template")}
                  color="primary" // Changed to primary color
                  variant="contained" // Changed to contained
                >
                  {t("button.submit")}
                </Button>
              </Box>
              )}
                            
            </Box>
            
            
            {selectedTemplate && (
              <Box
                display="grid"
                gap="20px"
                gridTemplateColumns={
                  isMobile ? "repeat(6, minmax(0, 1fr))" : "repeat(7, minmax(0, 1fr))"
                }
                sx={{
                  maxHeight: 'calc(60vh - 60px)', 
                  overflowY: 'auto',
                  width: "100%",
                  mb: 2,
                  background: "white",
                  borderRadius: 1,
                  border: "1px solid #e0e0e0",
                  boxShadow: "0px 0px 10px rgba(0,0,0,0.08)",
                  p: 1,
                }}
              >
                {/* Headers – identical to Availability */}
                <Typography
                  variant={isMobile ? "body2" : "h6"}
                  sx={{ gridColumn: "span 2", textAlign: "left", mt: 1, ml: "15px" }}
                >
                  <strong>{t("availabilty.weekday")}</strong>
                </Typography>

                {/* Rows – cloned from Availability */}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  {Array.from({ length: availabilityData.day_num }).map((_, rowIndex) => {
                    const currentDate = new Date(availabilityData.week_start);
                    currentDate.setDate(currentDate.getDate() + rowIndex);

                    const formattedDesktop = new Intl.DateTimeFormat("de", {
                      weekday: "long",
                    }).format(currentDate);

                    const formattedMobile = new Intl.DateTimeFormat("de", {
                      weekday: "long",
                    }).format(currentDate);

                    const isClosed =
                      availabilityData.closed_days &&
                      availabilityData.closed_days[rowIndex] === 1;

                    // derive current values
                    const openVal = values[`day_${rowIndex}_0`] || "";
                    const hasPersistedBreak = Boolean(
                      values[`day_${rowIndex}_2`] || values[`day_${rowIndex}_3`]
                    );
                    const hasBreak = breakOpen[rowIndex] || hasPersistedBreak;
                    const closeField = hasBreak
                      ? `day_${rowIndex}_3`
                      : `day_${rowIndex}_1`;
                    const closeVal = values[closeField] || "";
                    const bStart = values[`day_${rowIndex}_1`] || "";
                    const bEnd = values[`day_${rowIndex}_2`] || "";

                    const badOpenClose = openCloseInvalid(openVal, closeVal);
                    const badBreak =
                      hasBreak && breakInvalid(openVal, closeVal, bStart, bEnd);

                    // grey background flags
                    const openEmpty = !openVal;
                    const closeEmpty = !closeVal;
                    const bStartEmpty = !bStart;
                    const bEndEmpty = !bEnd;

                    // handlers (same as Availability)
                    const addBreakRow = () => {
                      setBreakOpen((prev) =>
                        prev.map((v, i) => (i === rowIndex ? true : v))
                      );
                      const close =
                        values[`day_${rowIndex}_3`] ||
                        values[`day_${rowIndex}_1`] ||
                        "";
                      setFieldValue(`day_${rowIndex}_3`, close); // final close to with-break slot
                      setFieldValue(`day_${rowIndex}_1`, ""); // break start
                      setFieldValue(`day_${rowIndex}_2`, ""); // break end
                    };

                    const removeBreakRow = () => {
                      setBreakOpen((prev) =>
                        prev.map((v, i) => (i === rowIndex ? false : v))
                      );
                      const finalClose =
                        values[`day_${rowIndex}_3`] ||
                        values[`day_${rowIndex}_1`] ||
                        "";
                      setFieldValue(`day_${rowIndex}_1`, finalClose); // move close back
                      setFieldValue(`day_${rowIndex}_2`, "");
                      setFieldValue(`day_${rowIndex}_3`, "");
                    };

                    const collapseBreakIfEmpty = () => {
                      const bS = values[`day_${rowIndex}_1`];
                      const bE = values[`day_${rowIndex}_2`];
                      const hasWithBreakClose = values[`day_${rowIndex}_3`];
                      if (!bS && !bE) {
                        if (hasWithBreakClose) removeBreakRow(); // move close back
                        else
                          setBreakOpen((p) =>
                            p.map((v, i) => (i === rowIndex ? false : v))
                          );
                      }
                    };

                    return (
                      <Box
                        key={`tmpl_row_${rowIndex}`}
                        display="grid"
                        gridTemplateColumns={
                          isMobile
                            ? "repeat(6, minmax(0, 1fr))"
                            : "repeat(7, minmax(0, 1fr))"
                        }
                        gap="10px"
                        sx={{
                          gridColumn: "span 7",
                          ml: 1,
                          mb: rowIndex === availabilityData.day_num - 1 ? 1 : 0,
                        }}
                      >
                        {/* Day label */}
                        <Typography
                          variant="h6"
                          sx={{
                            gridColumn: "span 2",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "left",
                            backgroundColor: "#f0f0f0",
                            borderRadius: 0.5,
                            p: 1,
                          }}
                        >
                          {isMobile ? formattedMobile : formattedDesktop}
                        </Typography>


                        {/* Closed days from calendar */}
                        {isClosed ? (
                          <Box
                            sx={{
                              gridColumn: isMobile ? "span 4" : "span 2",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#e0e0e0",
                              p: "10px",
                              borderRadius: "4px",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: "black", fontWeight: "bold" }}
                            >
                              {t("availabilty.closed")}
                            </Typography>
                          </Box>
                        ) : (
                          <>
                            {/* Open */}
                            <TimeField
                              label={t("availabilty.startime1")}
                              format="HH:mm"
                              value={toDayjs(openVal)}
                              onChange={(v) =>
                                setFieldValue(`day_${rowIndex}_0`, fromDayjs(v))
                              }
                              placeholder="hh:mm"
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  variant: "outlined",
                                  error: badOpenClose,
                                  disabled: values[`checkbox_${rowIndex}`],
                                  sx: (theme) => ({
                                    "& .MuiOutlinedInput-root": {
                                      bgcolor: openEmpty
                                        ? theme.palette.action.hover
                                        : theme.palette.background.paper,
                                    },
                                  }),
                                },
                              }}
                            />

                            {/* Close (single or with-break close) */}
                            <TimeField
                              label={t("availabilty.endtime1")}
                              format="HH:mm"
                              value={toDayjs(closeVal)}
                              onChange={(v) =>
                                setFieldValue(closeField, fromDayjs(v))
                              }
                              placeholder="hh:mm"
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  variant: "outlined",
                                  error: badOpenClose,
                                  disabled: values[`checkbox_${rowIndex}`],
                                  sx: (theme) => ({
                                    "& .MuiOutlinedInput-root": {
                                      bgcolor: closeEmpty
                                        ? theme.palette.action.hover
                                        : theme.palette.background.paper,
                                    },
                                  }),
                                },
                              }}
                            />

                            {/* Actions */}
                            <Box
                              sx={{
                                gridColumn: "span 1",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              {!hasBreak ? (
                                <Button
                                  size="small"
                                  startIcon={<AddCircleOutlineIcon />}
                                  onClick={addBreakRow}
                                  disabled={!openVal || !closeVal}
                                >
                                  {t("company.add_break") || "Pause hinzufügen"}
                                </Button>
                              ) : (
                                <Button
                                  size="small"
                                  color="warning"
                                  startIcon={<RemoveCircleOutlineIcon />}
                                  onClick={removeBreakRow}
                                >
                                  {t("company.remove_break") || "Pause entfernen"}
                                </Button>
                              )}
                            </Box>

                            {/* Second row: break fields */}
                            {hasBreak && (
                              <Box
                                sx={{
                                  gridColumn: "1 / -1",
                                  display: "grid",
                                  gridTemplateColumns: isMobile
                                    ? "repeat(6, minmax(0, 1fr))"
                                    : "repeat(7, minmax(0, 1fr))",
                                  gap: "10px",
                                  p: 1,
                                  borderRadius: 1,
                                  bgcolor: "rgba(0,0,0,0.03)",
                                  border: "1px dashed rgba(0,0,0,0.15)",
                                }}
                              >
                                {/* Spacer / label over weekday + holiday */}
                                <Typography
                                  variant="body2"
                                  sx={{
                                    gridColumn: isMobile ? "span 2" : "span 2",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "left",
                                    color: "text.secondary",
                                  }}
                                >
                                  {t("company.break")}
                                </Typography>

                                {/* Break start -> day_i_1 */}
                                <TimeField
                                  label={t("availabilty.breakFrom")}
                                  format="HH:mm"
                                  value={toDayjs(bStart)}
                                  onChange={(v) =>
                                    setFieldValue(`day_${rowIndex}_1`, fromDayjs(v))
                                  }
                                  onBlur={collapseBreakIfEmpty}
                                  placeholder="hh:mm"
                                  slotProps={{
                                    textField: {
                                      fullWidth: true,
                                      variant: "outlined",
                                      error: badBreak,
                                      disabled: values[`checkbox_${rowIndex}`],
                                      sx: (theme) => ({
                                        "& .MuiOutlinedInput-root": {
                                          bgcolor: bStartEmpty
                                            ? theme.palette.action.hover
                                            : theme.palette.background.paper,
                                        },
                                      }),
                                    },
                                  }}
                                />

                                {/* Break end -> day_i_2 */}
                                <TimeField
                                  label={t("availabilty.breakTill")}
                                  format="HH:mm"
                                  value={toDayjs(bEnd)}
                                  onChange={(v) =>
                                    setFieldValue(`day_${rowIndex}_2`, fromDayjs(v))
                                  }
                                  onBlur={collapseBreakIfEmpty}
                                  placeholder="hh:mm"
                                  slotProps={{
                                    textField: {
                                      fullWidth: true,
                                      variant: "outlined",
                                      error: badBreak,
                                      disabled: values[`checkbox_${rowIndex}`],
                                      sx: (theme) => ({
                                        "& .MuiOutlinedInput-root": {
                                          bgcolor: bEndEmpty
                                            ? theme.palette.action.hover
                                            : theme.palette.background.paper,
                                        },
                                      }),
                                    },
                                  }}
                                />

                                {!isMobile && <Box />}
                              </Box>
                            )}
                          </>
                        )}
                      </Box>
                    );
                  })}
                </LocalizationProvider>
              </Box>
            )}
            </Box>
            </Paper>
          </form>
        )}
      </Formik>
    </Box>
  );
};
const checkoutSchema = yup.object().shape({
  company_name: yup.string(),
  weekly_hours: yup.number(),
  shifts: yup.number(),
});
export default AvailabilityTemp;
